import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { AnimatePresence, motion } from 'framer-motion'
import BuyerIntakeStep1 from '@/components/buyer-intake/BuyerIntakeStep1'
import BuyerIntakeConfirmation from '@/components/buyer-intake/BuyerIntakeConfirmation'
import BuyerIntakeTransition from '@/components/buyer-intake/BuyerIntakeTransition'

type Step = 'matching' | 'transition' | 'confirmation'

const BuyerIntake = () => {
  usePageTitle('Buyer Registration')

  const { user, loading } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('matching')

  const [step1Data, setStep1Data] = useState({
    locations: [] as string[],
    budget: '',
    timeframe: '',
    businessTypes: [] as string[],
  })

  // Redirect non-authenticated users to sign up
  useEffect(() => {
    if (!loading && !user) {
      navigate('/sign-up')
    }
  }, [loading, user, navigate])

  // Transition timer → go to confirmation then dashboard
  useEffect(() => {
    if (step === 'transition') {
      const t = setTimeout(() => {
        toast({
          title: 'Profile Activated!',
          description: 'Your matching preferences have been saved. Redirecting to your dashboard...',
        })
        setStep('confirmation')
        setTimeout(() => navigate('/dashboard'), 2000)
      }, 1200)
      return () => clearTimeout(t)
    }
  }, [step, toast, navigate])

  const handleStep1Next = () => {
    // TODO: Store buyer preferences in a buyer_preferences table
    setStep('transition')
  }

  const slideVariants = {
    enter: { opacity: 0, x: 30 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 },
  }

  if (loading) return null

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-lg mx-auto">
          <Card className="shadow-medium border-border/50">
            <CardContent className="p-6 md:p-8">
              <AnimatePresence mode="wait">
                {step === 'matching' && (
                  <motion.div
                    key="step1"
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                  >
                    <BuyerIntakeStep1
                      data={step1Data}
                      onChange={setStep1Data}
                      onNext={handleStep1Next}
                    />
                  </motion.div>
                )}

                {step === 'transition' && (
                  <BuyerIntakeTransition key="transition" />
                )}

                {step === 'confirmation' && (
                  <BuyerIntakeConfirmation key="confirm" />
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default BuyerIntake
